/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag Fragment
 */
import type { FC, PropsWithChildren } from 'react';
// eslint-disable-next-line no-unused-vars -- Fragment is used in the code
import { useEffect, useRef, Fragment } from 'react';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';
import { cssMap } from '@compiled/react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import type { BoxProps } from '@atlaskit/primitives';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Flex, xcss } from '@atlaskit/primitives';
// eslint-disable-next-line no-unused-vars -- jsx is required for the code in this file
import { jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { ExitingPersistence } from '@atlaskit/motion';

import { maybeParseNativeEmbedParams } from '@atlassian/native-embeds-core/child-client';

import {
	CoverPictureQuery,
	type CoverPictureQuery$data,
	type CoverPictureQuery$variables,
} from '@confluence/page-cover-picture/entry-points/CoverPictureQuery.graphql.ts';
import { CoverPictureIdPropertyKey } from '@confluence/page-cover-picture/entry-points/pageCoverPictureTypes.ts';
import { propertyToId } from '@confluence/page-cover-picture/entry-points/coverPictureHelperFunctions.ts';
import { useApprovals } from '@confluence/approvals/entry-points/ApprovalsContext.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { EmojiTitleStateContainer } from '@confluence/emoji-title/entry-points/emojiTitleStateContainer.ts';
import { ContentStateStateContainer } from '@confluence/content-state/entry-points/ContentStateStateContainer.ts';
import { useEmbeddedViewPageFeatures } from '@confluence/embedded-confluence-allowed-features/entry-points/view-page.ts';
import { useIsEmbeddedConfluence_DO_NOT_USE } from '@confluence/embedded-confluence-check';
import { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useContentAppearance } from '@confluence/content-appearance/entry-points/ContentAppearanceContext.ts';
import {
	EmojiTitleQueryArtifact,
	type EmojiTitleQuery$data,
	type EmojiTitleQuery$variables,
} from '@confluence/emoji-title/entry-points/EmojiTitleQuery.ts';
import { getDraftShareIdFromQueryParam } from '@confluence/route-manager-utils/entry-points/getDraftShareIdFromQueryParam.ts';
import {
	getHasEmoji,
	getHasLiveDocsDefaultEmoji,
} from '@confluence/emoji-title/entry-points/transformer.ts';
import { useEmojiStatusFieldName } from '@confluence/emoji-title/entry-points/useEmojiStatusFieldName.ts';
import { useIsMarkdownMode } from '@confluence/content-markdown-mode/entry-points/useIsMarkdownMode.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { CopyWithRovoLoadable } from '@confluence/experiment-copy-with-rovo/entry-points/CopyWithRovoLoadable.ts';
import { useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';
import { expValEquals, expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { useSpaceHomepageId } from '@confluence/space-utils/entry-points/useSpaceHomepageId.ts';
import { usePageRestrictionsContext } from '@confluence/page-restrictions-context/entry-points/PageRestrictionsContext.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { SuggestWhiteboardOnPageTitleExperiment } from '@confluence/experiment-suggest-whiteboard-based-on-page-title/entry-points/SuggestWhiteboardOnPageTitleExperiment.ts';
import { SuggestAgentUsageFromPageTitleExperiment } from '@confluence/experiment-editor-title-area-banner/entry-points/EditorTitleAreaBanner.ts';

import { TitleSection } from './TitleSection.tsx';
// eslint-disable-next-line import/no-cycle
import { ToolbarAboveTitle } from './ToolbarAboveTitle.tsx';
import type { EditorTitleWithTitleAreaComponentsProps } from './titleareaContentComponentsTypes.ts';
import { AlignmentButtonContainer } from './AlignmentButtonContainer.tsx';

export const TOOLBAR_OPACITY_VAR: any = '--toolbar-opacity';

const editorTitleWrapperStyles = cssMap({
	root: {
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		[TOOLBAR_OPACITY_VAR]: 0,
		'&:hover': {
			[TOOLBAR_OPACITY_VAR]: 1,
		},
	},
	paddingTopSpace300: {
		paddingTop: token('space.300'),
	},
	paddingTopSpace0: {
		paddingTop: token('space.0'),
	},
	topSpacing: {
		marginTop: token('space.negative.400'),
		paddingTop: token('space.200'),
	},
	paddingBottomSpace300: {
		paddingBottom: token('space.300'),
	},
	newEditorTitleHoverTopPadding: {
		paddingTop: token('space.250'),
	},
	// When the page is a live doc rendered inside a native embed (and there is
	// no cover picture to provide natural top spacing), animate in a top padding
	// on hover so that:
	//  - the live doc toolbar (which fades in on hover via TOOLBAR_OPACITY_VAR)
	//    is pushed down out of the way of the host's native embed header, and
	//  - when not hovered, no extra whitespace is shown above the title.
	// This is co-located with the toolbar's hover styles so the two animations
	// stay perfectly in sync.
	nativeEmbedLiveDocHoverPadding: {
		paddingTop: token('space.0'),
		transition: 'padding-top 150ms ease',
		// :has() is baseline-supported across evergreen browsers and is required
		// to keep the padding applied while the toolbar is in a persistent-visible
		// state (open menu / focused button) but the wrapper itself is no longer
		// :hovered (e.g. user moved the mouse onto the host's native embed header).
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'&:hover, &:has([data-toolbar-visible="true"])': {
			paddingTop: token('space.800'),
		},
	},
});

const titleBoxStylesAlignSelfCenter = xcss({
	alignSelf: 'center',
});

const authorBylinePositionRelative = xcss({
	position: 'relative',
});

const editorTitleBaseStyles = xcss({
	width: '100%',
	marginTop: 'space.negative.200',
	marginBottom: 'space.400',
});

const editorTitleSmallerMarginBottomBaseStyles = xcss({
	marginBottom: 'space.300',
});

type EditorTitleWithButtonsWrapperProps = {
	viewMode: boolean;
	shouldBylineRenderContentTopper: boolean;
	isContentTopperComponentPresent: boolean;
	hasCoverPicture: boolean;
	isInNativeEmbedLiveDoc: boolean;
} & BoxProps<'div'>;

const EditorTitleWithButtonsWrapper: FC<PropsWithChildren<EditorTitleWithButtonsWrapperProps>> = ({
	viewMode,
	isContentTopperComponentPresent,
	shouldBylineRenderContentTopper,
	hasCoverPicture,
	isInNativeEmbedLiveDoc,
	children,
}) => {
	const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();

	return (
		<div
			// TODO: Cleanup CSS when cleaning up FG PGXT-8139
			css={[
				editorTitleWrapperStyles.root,
				viewMode
					? editorTitleWrapperStyles.paddingTopSpace300
					: editorTitleWrapperStyles.paddingTopSpace0,
				isContentTopperComponentPresent && editorTitleWrapperStyles.topSpacing,
				isContentTopperComponentPresent &&
					shouldBylineRenderContentTopper &&
					editorTitleWrapperStyles.paddingBottomSpace300,
				isContentTopperComponentPresent &&
					!hasCoverPicture &&
					editorTitleWrapperStyles.newEditorTitleHoverTopPadding,
				isInNativeEmbedLiveDoc &&
					!hasCoverPicture &&
					editorTitleWrapperStyles.nativeEmbedLiveDocHoverPadding,
			]}
			data-testid="editor-title-with-buttons-div"
			{...ssrPlaceholderIdProp}
		>
			{children}
		</div>
	);
};
EditorTitleWithButtonsWrapper.displayName = 'EditorTitleWithButtonsWrapper';

export const EditorTitleWithTitleAreaComponents = ({
	contentId,
	isPublishingContent,
	contentType,
	userOffline,
	isTitleDisabled,
	linkCreation,
	isUnpublishedDraft,
	children: editorTitle,
	isTemplateEditor = false,
	isTitleCenterAligned,
	viewMode,
	contentTopperComponent,
	contentClassificationMetadata,
	hasClassification,
	spaceKey,
	isEditorEmpty,
	isTitleAlignmentLoading,
	contentComponentsDisabled,
	isDraftLockedForEditing,
	isStatusDisconnected,
	coverPictureState,
	title,
	titleAfterBlur,
	isLivePage,
	coverImageWidthCached,
	hideSuggestedTitle = false,
	hideContentState = false,
	hideEditorAppearanceButton = false,
	hideContentModeButton = false,
	hideAlignmentButton = false,
	pageTitleContent,
	aiHeaderImageStatus,
	aiHeaderImageFileId,
	aiHeaderImageError,
	generateAIHeaderImage,
	setAIHeaderImageStatus,
	autoPageEmojiCohort,
	emojiTitleDiscovered,
	version,
	hasTitleChangedRef,
	isSmartCreate,
}: EditorTitleWithTitleAreaComponentsProps) => {
	const { contentAppearance } = useContentAppearance();
	const isPageContentFullWidth = contentAppearance.appearance === 'full-width';
	const emojiStatusFieldName = useEmojiStatusFieldName();
	const homepageId = useSpaceHomepageId(spaceKey);
	const isSpaceOverview = homepageId === contentId;
	const { hideTitleToolbar } = useEmbeddedViewPageFeatures();
	const isEmbeddedConfluence = useIsEmbeddedConfluence_DO_NOT_USE();
	const showTitleToolbar = isEmbeddedConfluence ? !hideTitleToolbar : true;

	const { approvalRequest, availableWorkflowStatuses } = useApprovals();
	const hasActiveWorkflowStatus =
		!!approvalRequest?.application?.workflowStatusId &&
		availableWorkflowStatuses.some((s) => s.id === approvalRequest.application.workflowStatusId) &&
		expValEquals('confluence_approvals_eap', 'isEnabled', true);
	const { data } = useQueryInterop<CoverPictureQuery$data, CoverPictureQuery$variables>(
		CoverPictureQuery,
		{
			fetchPolicy: window.__SSR_RENDERED__ ? 'cache-first' : 'cache-and-network',
			context: { single: true },
			variables: {
				contentId,
				status: ['current', 'draft'],
				draftShareId: getDraftShareIdFromQueryParam(),
			},
		},
	);

	const { data: emojiData, error: emojiError } = useQueryInterop<
		EmojiTitleQuery$data,
		EmojiTitleQuery$variables
	>(EmojiTitleQueryArtifact, {
		variables: {
			contentId,
			status: ['current', 'draft'],
			skipPublishedFragment: false,
			draftShareId: getDraftShareIdFromQueryParam(),
		},
	});

	const hasDefaultTitleEmoji = isLivePage && getHasLiveDocsDefaultEmoji(emojiData);
	const hasEmoji = !!(
		emojiData &&
		(getHasEmoji(emojiData, emojiStatusFieldName) || hasDefaultTitleEmoji)
	);

	const properties = data?.content?.nodes?.[0]?.properties?.nodes || [];
	// we only need the draft info since this will just be used to determine what component to show during edit mode
	const draft = properties.find((p) => p?.key === CoverPictureIdPropertyKey.DRAFT);
	const coverPictureInfo = propertyToId(draft);
	const hasCoverPicture = !!coverPictureInfo?.value?.id;
	const isUnsplash = coverPictureInfo?.value?.id.includes('images.unsplash.com');
	const isGeneratingAIImage =
		aiHeaderImageStatus === 'LOADING' || aiHeaderImageStatus === 'LOADING_WITH_ACCEPTANCE_FLOW';

	const aiCoverPictureInFlightRef = useRef(false);

	// Track when AI generation starts — keep it true until the cover picture is actually committed
	if (isGeneratingAIImage) {
		aiCoverPictureInFlightRef.current = true;
	}
	// Clear the in-flight flag once the cover picture is confirmed via GraphQL
	if (hasCoverPicture) {
		aiCoverPictureInFlightRef.current = false;
	}

	// Clear the in-flight flag for errors
	if (aiHeaderImageStatus === 'ERROR') {
		aiCoverPictureInFlightRef.current = false;
	}

	// Clear the in-flight flag if the AI cover picture failed to generate, no error
	if (
		!hasCoverPicture &&
		!aiHeaderImageFileId &&
		!isGeneratingAIImage &&
		!coverPictureState.state.isUploadingPicture &&
		aiHeaderImageStatus === 'NOT_LOADING'
	) {
		aiCoverPictureInFlightRef.current = false;
	}

	const effectiveHasCoverPicture = hasCoverPicture || aiCoverPictureInFlightRef.current;

	const isLiveDoc = useIsCurrentPageLive();
	const isNativeEmbed = !!maybeParseNativeEmbedParams(new URLSearchParams(window.location.search));
	const isInNativeEmbedLiveDoc = isNativeEmbed && isLiveDoc;

	useEffect(() => {
		void coverPictureState.setHasCoverPicture(hasCoverPicture);
	}, [hasCoverPicture, coverPictureState]);

	const isMarkdownMode = useIsMarkdownMode(isLivePage ? contentId : undefined);
	const shouldRenderAboveTitle =
		!isTemplateEditor &&
		showTitleToolbar &&
		!(isMarkdownMode && fg('platform_editor_remove_titlebar_options_md'));
	const shouldBylineRenderContentTopper = isLivePage || !isSpaceOverview;

	const { loading: restrictionsLoading, restrictions } = usePageRestrictionsContext();
	const hasAnyRestrictions = !!(
		restrictions?.hasRestrictions ||
		restrictions?.hasViewRestrictions ||
		restrictions?.hasInheritedRestrictions
	);
	// Hide CopyWithRovo while restrictions are loading or unknown to avoid leaking
	// the affordance for restricted pages before data has resolved.
	const shouldRenderCopyWithRovo = !restrictionsLoading && !hasAnyRestrictions;

	return (
		<>
			{emojiError ? <ErrorDisplay error={emojiError} /> : null}
			<Subscribe
				to={[
					EmojiTitleStateContainer,
					ContentStateStateContainer,
					AlignmentButtonContainer,
					FlagsStateContainer,
				]}
			>
				{(
					emojiTitleState: EmojiTitleStateContainer,
					contentStateState: ContentStateStateContainer,
					alignmentButtonState: AlignmentButtonContainer,
					flags: FlagsStateContainer,
				) => (
					<Flex
						direction="column"
						xcss={[
							editorTitleBaseStyles,
							shouldBylineRenderContentTopper && editorTitleSmallerMarginBottomBaseStyles,
						]}
					>
						<EditorTitleWithButtonsWrapper
							viewMode={!!viewMode}
							isContentTopperComponentPresent={!!contentTopperComponent}
							shouldBylineRenderContentTopper={shouldBylineRenderContentTopper}
							hasCoverPicture={effectiveHasCoverPicture}
							isInNativeEmbedLiveDoc={isInNativeEmbedLiveDoc}
						>
							{shouldRenderAboveTitle ? (
								<ToolbarAboveTitle
									contentId={contentId}
									userOffline={userOffline}
									isUnpublishedDraft={isUnpublishedDraft}
									isTitleDisabled={isTitleDisabled}
									isAddHeaderImageButtonFocused={
										coverPictureState.state.isAddHeaderImageButtonFocused
									}
									isCoverPictureModalOpen={coverPictureState.state.isModalOpen}
									hasCoverPicture={effectiveHasCoverPicture}
									isUploadingPicture={coverPictureState.state.isUploadingPicture}
									setSelectedEmoji={emojiTitleState.setSelectedEmoji}
									onEmojiPickerLoaded={emojiTitleState.onEmojiPickerLoaded}
									isEmojiButtonFocused={emojiTitleState.state.isEmojiButtonFocused}
									onUpdateStartEmoji={emojiTitleState.onUpdateStart}
									onUpdateEndEmoji={emojiTitleState.onUpdateEnd}
									contentType={contentType}
									isPublishingContent={isPublishingContent}
									contentStateState={contentStateState}
									hasContentState={
										!!contentStateState.state.contentState || hasActiveWorkflowStatus
									}
									isSpaceOverview={isSpaceOverview}
									isTitleCenterAligned={isTitleCenterAligned}
									alignmentButtonState={alignmentButtonState}
									coverPictureState={coverPictureState}
									// need to pass in the state value directly or the component will not re-render
									coverPictureWidth={coverPictureState.state.coverPictureWidth}
									isSecondEmojiPickerOpen={emojiTitleState.state.openSecondEmojiPicker}
									flags={flags}
									contentClassificationMetadata={contentClassificationMetadata}
									hasClassification={hasClassification}
									isReadOnly={viewMode}
									spaceKey={spaceKey}
									isEditorEmpty={isEditorEmpty}
									contentComponentsDisabled={contentComponentsDisabled}
									isDraftLockedForEditing={isDraftLockedForEditing}
									isStatusDisconnected={isStatusDisconnected}
									hasEmoji={hasEmoji}
									coverImageWidthCached={coverImageWidthCached}
									isUnsplash={isUnsplash}
									hideSuggestedTitle={hideSuggestedTitle}
									hideContentState={hideContentState}
									hideEditorAppearanceButton={hideEditorAppearanceButton}
									hideContentModeButton={hideContentModeButton}
									hideAlignmentButton={hideAlignmentButton}
									aiHeaderImageStatus={aiHeaderImageStatus}
									aiHeaderImageFileId={aiHeaderImageFileId}
									aiHeaderImageError={aiHeaderImageError}
									generateAIHeaderImage={generateAIHeaderImage}
									setAIHeaderImageStatus={setAIHeaderImageStatus}
									isSmartCreate={isSmartCreate}
									autoPageEmojiCohort={autoPageEmojiCohort}
									emojiTitleDiscovered={emojiTitleDiscovered}
								/>
							) : null}
							<TitleSection
								editorTitle={editorTitle}
								contentTopperComponent={contentTopperComponent}
								contentId={contentId}
								isTemplateEditor={isTemplateEditor}
								onUpdateStartEmoji={emojiTitleState.onUpdateStart}
								onUpdateEndEmoji={emojiTitleState.onUpdateEnd}
								isTitleDisabled={isTitleDisabled}
								isTitleCenterAligned={isTitleCenterAligned}
								linkCreation={linkCreation}
								hasCoverPicture={effectiveHasCoverPicture}
								hasEmoji={hasEmoji}
								coverPictureWidth={coverPictureState.state.coverPictureWidth}
								isRemovingEmoji={emojiTitleState.state.isRemovingEmoji}
								isReadOnly={viewMode}
								contentClassificationMetadata={contentClassificationMetadata}
								isPageContentFullWidth={isPageContentFullWidth}
								hasClassification={hasClassification}
								isTitleAlignmentLoading={isTitleAlignmentLoading}
								isSpaceOverview={isSpaceOverview}
								pageTitleContent={pageTitleContent} // Prop for contextualized emoji
								autoPageEmojiCohort={autoPageEmojiCohort}
								isUnpublishedDraft={isUnpublishedDraft}
								isEditorEmpty={isEditorEmpty}
								emojiTitleDiscovered={emojiTitleDiscovered}
								contentType={contentType}
							/>
						</EditorTitleWithButtonsWrapper>
						{fg('cc_fd_suggest_whiteboards_on_page_title_gate') ? (
							<Box xcss={authorBylinePositionRelative}>
								{shouldBylineRenderContentTopper && contentTopperComponent && (
									<Box
										testId="content-topper-wrapper"
										xcss={[isTitleCenterAligned && titleBoxStylesAlignSelfCenter]}
									>
										{contentTopperComponent}
									</Box>
								)}
								<ExitingPersistence appear>
									<SuggestWhiteboardOnPageTitleExperiment
										spaceKey={spaceKey}
										contentId={contentId}
										title={title}
										titleAfterBlur={titleAfterBlur}
										isLivePage={isLivePage}
										isUnpublishedDraft={isUnpublishedDraft}
										version={version}
										hasTitleChangedRef={hasTitleChangedRef}
									/>
								</ExitingPersistence>
								{fg('cc-fd-skill-rec-from-page-title-gate') && (
									<ExitingPersistence appear>
										<SuggestAgentUsageFromPageTitleExperiment
											spaceKey={spaceKey}
											contentId={contentId}
											title={title}
											titleAfterBlur={titleAfterBlur}
											isLivePage={isLivePage}
											isUnpublishedDraft={isUnpublishedDraft}
											version={version}
											hasTitleChangedRef={hasTitleChangedRef}
										/>
									</ExitingPersistence>
								)}
							</Box>
						) : (
							shouldBylineRenderContentTopper &&
							contentTopperComponent && (
								<Box
									testId="content-topper-wrapper"
									xcss={[isTitleCenterAligned && titleBoxStylesAlignSelfCenter]}
								>
									{contentTopperComponent}
								</Box>
							)
						)}
						{shouldRenderCopyWithRovo &&
							expVal('cc_fd_copy_with_rovo_gate', 'isEnabled', false) && (
								<CopyWithRovoLoadable contentId={contentId} />
							)}
					</Flex>
				)}
			</Subscribe>
		</>
	);
};
EditorTitleWithTitleAreaComponents.displayName = 'EditorTitleWithTitleAreaComponents';
